import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWisdomCard } from '../hooks/useWisdomCard';
import { X, Download, Share2, Square, RectangleVertical, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Modal component for sharing Sophia's wisdom as beautiful cards
 */
export function ShareWisdomModal({ 
  isOpen, 
  onClose, 
  quote, 
  language = 'en' 
}) {
  const { isGenerating, generateCard, downloadCard, shareCard } = useWisdomCard();
  const [format, setFormat] = useState('square');
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const t = (key) => {
    const translations = {
      'title': language === 'de' ? 'Weisheit teilen' : 'Share Wisdom',
      'subtitle': language === 'de' ? 'Erstelle eine schöne Karte zum Teilen' : 'Create a beautiful card to share',
      'format': language === 'de' ? 'Format wählen' : 'Choose format',
      'square': language === 'de' ? 'Quadrat' : 'Square',
      'story': language === 'de' ? 'Story' : 'Story',
      'squareDesc': language === 'de' ? 'Instagram Post' : 'Instagram Post',
      'storyDesc': language === 'de' ? 'Instagram/WhatsApp Story' : 'Instagram/WhatsApp Story',
      'download': language === 'de' ? 'Herunterladen' : 'Download',
      'share': language === 'de' ? 'Teilen' : 'Share',
      'generating': language === 'de' ? 'Wird erstellt...' : 'Creating...',
      'preview': language === 'de' ? 'Vorschau' : 'Preview',
    };
    return translations[key] || key;
  };
  
  // Generate preview when format changes
  useEffect(() => {
    if (isOpen && quote) {
      setPreviewUrl(null);
      generateCard(quote, format).then(url => {
        setPreviewUrl(url);
      }).catch(console.error);
    }
  }, [isOpen, quote, format, generateCard]);
  
  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setPreviewUrl(null);
      setFormat('square');
    }
  }, [isOpen]);
  
  const handleDownload = async () => {
    await downloadCard(quote, format, 'sophia-wisdom');
  };
  
  const handleShare = async () => {
    await shareCard(quote, format);
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-background rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
            <div>
              <h2 className="font-display text-xl font-semibold">{t('title')}</h2>
              <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Format selector */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-muted-foreground">{t('format')}</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="wisdom-format-square"
                  onClick={() => setFormat('square')}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    format === 'square'
                      ? 'border-sophia-pink-500 bg-sophia-pink-500/10'
                      : 'border-border hover:border-sophia-pink-500/50'
                  }`}
                >
                  <Square className={`w-8 h-8 ${format === 'square' ? 'text-sophia-pink-500' : 'text-muted-foreground'}`} />
                  <div className="text-center">
                    <p className={`font-medium text-sm ${format === 'square' ? 'text-sophia-pink-500' : 'text-foreground'}`}>
                      {t('square')}
                    </p>
                    <p className="text-xs text-muted-foreground">{t('squareDesc')}</p>
                  </div>
                </button>
                
                <button
                  data-testid="wisdom-format-story"
                  onClick={() => setFormat('story')}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    format === 'story'
                      ? 'border-sophia-pink-500 bg-sophia-pink-500/10'
                      : 'border-border hover:border-sophia-pink-500/50'
                  }`}
                >
                  <RectangleVertical className={`w-8 h-8 ${format === 'story' ? 'text-sophia-pink-500' : 'text-muted-foreground'}`} />
                  <div className="text-center">
                    <p className={`font-medium text-sm ${format === 'story' ? 'text-sophia-pink-500' : 'text-foreground'}`}>
                      {t('story')}
                    </p>
                    <p className="text-xs text-muted-foreground">{t('storyDesc')}</p>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Preview */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">{t('preview')}</label>
              <div className={`relative bg-muted/30 rounded-2xl overflow-hidden flex items-center justify-center ${
                format === 'story' ? 'aspect-[9/16] max-h-[400px]' : 'aspect-square'
              }`}>
                {isGenerating || !previewUrl ? (
                  <div className="flex flex-col items-center gap-3 text-muted-foreground">
                    <Loader2 className="w-8 h-8 animate-spin text-sophia-pink-500" />
                    <span className="text-sm">{t('generating')}</span>
                  </div>
                ) : (
                  <img 
                    src={previewUrl} 
                    alt="Wisdom Card Preview" 
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Footer with actions */}
          <div className="flex gap-3 px-6 py-4 border-t border-border/50 bg-muted/20">
            <Button
              data-testid="wisdom-download-btn"
              onClick={handleDownload}
              disabled={isGenerating || !previewUrl}
              variant="outline"
              className="flex-1 h-12 rounded-full border-sophia-pink-500/30 text-sophia-pink-500 hover:bg-sophia-pink-500/10 gap-2"
            >
              <Download className="w-4 h-4" />
              {t('download')}
            </Button>
            <Button
              data-testid="wisdom-share-btn"
              onClick={handleShare}
              disabled={isGenerating || !previewUrl}
              className="flex-1 h-12 rounded-full bg-sophia-pink-500 hover:bg-sophia-pink-600 text-white gap-2"
            >
              <Share2 className="w-4 h-4" />
              {t('share')}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Simple button to trigger the share wisdom modal
 */
export function ShareWisdomButton({ 
  quote, 
  language = 'en',
  variant = 'icon', // 'icon' or 'full'
  className = '' 
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const t = (key) => {
    const translations = {
      'shareCard': language === 'de' ? 'Als Karte teilen' : 'Share as Card',
    };
    return translations[key] || key;
  };
  
  if (variant === 'icon') {
    return (
      <>
        <motion.button
          data-testid="share-wisdom-btn"
          onClick={() => setIsModalOpen(true)}
          className={`w-9 h-9 rounded-full bg-sophia-pink-500/10 flex items-center justify-center text-sophia-pink-500 hover:bg-sophia-pink-500/20 transition-colors ${className}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title={t('shareCard')}
        >
          <Share2 className="w-4 h-4" />
        </motion.button>
        <ShareWisdomModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          quote={quote}
          language={language}
        />
      </>
    );
  }
  
  return (
    <>
      <motion.button
        data-testid="share-wisdom-btn"
        onClick={() => setIsModalOpen(true)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full bg-sophia-pink-500/10 text-sophia-pink-500 hover:bg-sophia-pink-500/20 transition-colors text-sm font-medium ${className}`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Share2 className="w-4 h-4" />
        {t('shareCard')}
      </motion.button>
      <ShareWisdomModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        quote={quote}
        language={language}
      />
    </>
  );
}
